import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { DateInput, Select } from '../../../../components/ui';
import { acctToolbarLabel } from '../../shared/typography';

export const FieldLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const { colors } = useTheme();
  return (
    <span className={cn('mb-0.5 block text-sm font-medium leading-tight', colors.text, className)}>
      {children}
    </span>
  );
};

export const ToolbarField: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className,
}) => {
  const { colors } = useTheme();
  return (
    <div className={cn('flex items-center gap-2 shrink-0', className)}>
      <span className={cn(acctToolbarLabel, colors.text)}>
        {label}
      </span>
      {children}
    </div>
  );
};

type SelectOption = { value: string; label: string };

type PeriodDateRangeFieldsProps = {
  periodLabel?: string;
  periodId: string;
  periodOptions: SelectOption[];
  onPeriodChange: (value: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  periodClassName?: string;
  selectClassName?: string;
};

/** Period select + date range (date inputs only when Custom period is selected). */
export const PeriodDateRangeFields: React.FC<PeriodDateRangeFieldsProps> = ({
  periodLabel = 'Accounting period',
  periodId,
  periodOptions,
  onPeriodChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  periodClassName = 'min-w-[13rem] max-w-xs',
  selectClassName,
}) => {
  const { colors } = useTheme();
  const showDateRange = !periodId;
  /** Keep period Select and DateInputs the same control height. */
  const controlH = 'h-9 !min-h-9 !py-0 box-border';

  return (
    <>
      <div className={periodClassName}>
        <FieldLabel>{periodLabel}</FieldLabel>
        <Select
          value={periodId}
          onChange={(e) => onPeriodChange(e.target.value)}
          options={periodOptions}
          className={cn(
            '[&_[role=combobox]]:h-9 [&_[role=combobox]]:!min-h-9 [&_[role=combobox]]:!py-0 [&_[role=combobox]]:px-2',
            selectClassName,
          )}
        />
      </div>
      {showDateRange && (
        <div>
          <FieldLabel>Date range</FieldLabel>
          <div className="flex items-center gap-2">
            <DateInput
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
              showFormatHint={false}
              className={controlH}
            />
            <span className={cn('text-sm', colors.textMuted)}>to</span>
            <DateInput
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              showFormatHint={false}
              className={controlH}
            />
          </div>
        </div>
      )}
    </>
  );
};

type PeriodDateRangeToolbarProps = {
  periodLabel?: string;
  dateRangeLabel?: string;
  periodId: string;
  periodOptions: SelectOption[];
  onPeriodChange: (value: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  periodSelectClassName?: string;
  dateInputClassName?: string;
};

/** Inline toolbar variant for compact report headers (e.g. ledger kartela). */
export const PeriodDateRangeToolbar: React.FC<PeriodDateRangeToolbarProps> = ({
  periodLabel = 'Period',
  dateRangeLabel = 'Date range',
  periodId,
  periodOptions,
  onPeriodChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  periodSelectClassName = 'w-[10.5rem]',
  dateInputClassName = 'w-[8.25rem]',
}) => {
  const { colors } = useTheme();
  const showDateRange = !periodId;

  return (
    <>
      <ToolbarField label={periodLabel}>
        <div className={periodSelectClassName}>
          <Select value={periodId} onChange={(e) => onPeriodChange(e.target.value)} options={periodOptions} />
        </div>
      </ToolbarField>
      {showDateRange && (
        <ToolbarField label={dateRangeLabel}>
          <div className="flex items-center gap-1.5">
            <div className={dateInputClassName}>
              <DateInput
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                showFormatHint={false}
              />
            </div>
            <span className={cn('text-xs shrink-0', colors.textMuted)}>to</span>
            <div className={dateInputClassName}>
              <DateInput
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                showFormatHint={false}
              />
            </div>
          </div>
        </ToolbarField>
      )}
    </>
  );
};
